// Адреса экранов приложения (memory-router). Только id — данные экран берёт сам.
/** Параметры экранов сущностей (HQ-194): ?tab= — вкладка tab_bar, ?season= — id сезона турнира. Экран читает их как начальные. */
export interface EntityParams { tab?: string; season?: number }
function withQuery(path: string, params: EntityParams = {}): string {
  const q = new URLSearchParams();
  if (params.tab) q.set('tab', params.tab);
  if (params.season != null) q.set('season', String(params.season));
  const s = q.toString();
  return s ? `${path}?${s}` : path;
}

export const paths = {
  match: (id: number | string) => `/match/${id}`,
  team: (id: number | string, params?: Pick<EntityParams, 'tab'>) => withQuery(`/team/${id}`, params),
  player: (id: number | string, params?: Pick<EntityParams, 'tab'>) => withQuery(`/player/${id}`, params),
  league: (id: number | string, params?: EntityParams) => withQuery(`/league/${id}`, params),
  venue: (id: number | string) => `/venue/${id}`,
  rankings: () => '/rankings',
  /** endpoint — адрес поиска из серверной шапки (action.endpoint), если есть. */
  search: (endpoint?: string) => (endpoint ? `/search?path=${encodeURIComponent(endpoint)}` : '/search'),
  compare: (ids: number[]) => `/compare?players=${ids.join(',')}`,
  /** Поиск в режиме выбора игрока для сравнения: выбранный id дописывается к ids и открывается сравнение. */
  pickCompare: (ids: number[]) => `/search?pick=player&with=${ids.join(',')}`,
  /** Сравнение двух команд (HQ-2331): пара в порядке показа, формат — key сервера нижним регистром (нет — по умолчанию). */
  compareTeams: (ids: number[], format?: string | null) =>
    `/compare-teams?teams=${ids.join(',')}${format ? `&format=${encodeURIComponent(format.toLowerCase())}` : ''}`,
  /** Поиск в режиме выбора соперника: только команды того же вида (national), без самой команды with. */
  pickTeam: (withId: number, national: boolean) => `/search?pick=team&with=${withId}&national=${national ? 1 : 0}`,
  /** Пейвол подписки «без рекламы» за Stars (HQ-1091); source — откуда пришли (значение справочника source словаря). */
  premium: (source?: string) => (source ? `/premium?source=${encodeURIComponent(source)}` : '/premium'),
  /** Квиз дня (HQ-2329): endpoint — адрес набора из кнопки шапки (action.endpoint) как есть. */
  quiz: (endpoint?: string) => (endpoint ? `/quiz?path=${encodeURIComponent(endpoint)}` : '/quiz'),
  records: (params: { league?: number; season?: number; team?: number } = {}) => {
    const q = Object.entries(params).filter(([, v]) => v != null).map(([k, v]) => `${k}=${v}`).join('&');
    return q ? `/records?${q}` : '/records';
  },
};

/**
 * Серверный адрес «ещё» (more_path, API) → экран приложения. Адрес не пересобираем: только узнаём, куда он ведёт.
 * Незнакомый → null (кнопку «ещё» не рисуем).
 */
export function routeFromApiPath(apiPath: string | null | undefined): { path: string; tab?: string } | null {
  if (!apiPath) return null;
  const [path = '', query = ''] = apiPath.split('?');
  const m = /\/screens\/cricket\/(.+?)\/?$/.exec(path);
  if (!m) return null;
  const parts = m[1]!.split('/');
  const entity: Record<string, (id: string) => string> = {
    tournament: paths.league, team: paths.team, player: paths.player, match: paths.match, venue: paths.venue,
  };
  // Третий сегмент — вкладка tab_bar сущности (ключи — be/apps/cricket/tab_bar.py; HQ-1738: «Лидеры» → …/tournament/N/stats/).
  const entityTabs: Record<string, (id: string, tab: string) => string> = {
    tournament: (id, tab) => paths.league(id, { tab }), team: (id, tab) => paths.team(id, { tab }),
    player: (id, tab) => paths.player(id, { tab }),
  };
  const knownTabs: Record<string, string[]> = {
    tournament: ['overview', 'matches', 'table', 'stats', 'squads'],
    team: ['overview', 'matches', 'squad', 'records', 'news'],
    player: ['profile', 'matches', 'career'],
  };
  if (parts.length >= 2 && entity[parts[0]!] && /^\d+$/.test(parts[1]!)) {
    const tab = parts[2];
    if (tab && knownTabs[parts[0]!]?.includes(tab)) return { path: entityTabs[parts[0]!]!(parts[1]!, tab) };
    return { path: entity[parts[0]!]!(parts[1]!) };
  }
  switch (parts.join('/')) {
    case 'matches': return { path: '/tab/matches' };
    case 'leagues': return { path: '/tab/leagues' };
    case 'news/hub': case 'news': return { path: '/tab/news' };
    case 'favorites': return { path: '/tab/favorites', tab: new URLSearchParams(query).get('tab') ?? undefined };
    case 'rankings': return { path: paths.rankings() };
    case 'highlights': {
      const league = new URLSearchParams(query).get('league');
      return { path: league && /^\d+$/.test(league) ? `/videos?league=${league}` : '/videos' };
    }
    case 'home': return { path: '/tab/home' };
    default: return null;
  }
}
